<template>
  <ValidationProvider
    :rules="{
      ...rules,
      filesHaveNoErrors: true,
      filesMaxTotalSize: 20
    }"
    mode="aggressive"
    v-bind="validationAttrs"
    v-slot="{ classes, errors, failedRules }"
    tag="div"
    ref="valProvider"
  >
    <div v-show-slide="!!errors.length"
      :id="uniqName('errors')"
    >
      <AlertMessage type="danger" slim>
        {{ errors[0] }}.
        <span v-if="failedRules.filesHaveNoErrors"
        >Remove all invalid files before proceeding.</span>
      </AlertMessage>
    </div>

    <FileAttachments
      :aria-describedby="uniqName('errors')"
      :aria-invalid="errors.length ? 'true' : 'false'"
      :aria-required="rules.required ? 'true' : null"
      :attachButtonClass="attachButtonClass"
      file-row-class="file-attach-row"
      files-container-class="bg-primary-lighter p-2 small"
      :processNewFile="processFile"
      removeButtonClass="btn btn-icon"
      :class="classes"
      v-bind="{ ...fileAttachmentProps, ...$attrs }"
      v-model="model"
    >
      <template #attach-button-label>
        <slot name="attach-button-label"></slot>
      </template>

      <template #file-info="{ file }">
        <ReflectAttrs :sizeParts="filesize(file.size).split(' ')" v-slot="{ sizeParts }">
          <div class="opacity-75 text-nowrap">
            {{ sizeParts[0] }}
            <span class="file-size-unit">{{ sizeParts[1] }}</span>
          </div>
        </ReflectAttrs>
      </template>

      <template #file-error="{ file }">
        <span class="text-danger">
          <span class="bi bi-exclamation-circle"></span>
          <span class="visually-hidden">Error: </span>
          {{ file.error }}
        </span>
      </template>
      <template #remove-button-content="{ file }">
        <span class="bi bi-x"></span>
        <span class="visually-hidden">Remove {{ file.name }}</span>
      </template>
    </FileAttachments>
  </ValidationProvider>
</template>

<script>
import filesize from 'filesize';
import FileAttachments from '@ssi/vue-common/src/components/formControls/FileAttachments';
import AlertMessage from '@/components/AlertMessage';
import { pick } from '@/helpers/util';

const allowedFileTypes = [
  'application/msword', // .doc
  'application/pdf', // .pdf
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'image/gif', // .gif
  'image/jpeg', // .jpg, .jpeg
  'image/png', // .png
  'text/plain' // .txt
];

export default {
  components: { AlertMessage, FileAttachments },

  props: {
    ...FileAttachments.props,

    rules: {
      type: Object,
      default: () => ({})
    },

    /**
     * Attributes/props to pass to the VeeValidate ValidationProvider.
     */
    validationAttrs: {
      type: Object,
      default: () => ({})
    },

    value: {
      type: Array,
      required: true
    }
  },

  computed: {
    fileAttachmentProps: ({ $props }) => pick($props, Object.keys(FileAttachments.props)),

    model: {
      get() { return this.value; },
      set(value) { this.$emit('input', value); }
    }
  },

  methods: {
    filesize: filesize.partial({ round: 1, standard: 'jedec' }),

    async processFile(file) {
      if (!allowedFileTypes.includes(file.type)) {
        throw new Error('File type not allowed.');
      }

      await this.processNewFile(file);

      const encoded = await new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        } catch (e) {
          reject(e);
          this.$emit('processing-failed');
        }
      });

      this.$emit('processing-complete');

      return {
        name: file.name,
        size: file.size,
        contentType: file.type,
        contents: encoded,
        // Create a URL so the temporary file can be downloaded if desired
        url: window.URL.createObjectURL(file)
      };
    },

    validate() { return this.$refs.valProvider.validate(); }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";

::v-deep {
  .file-attach-row {
    a {
      text-decoration: none;
    }

    &:hover {
      background-color: $primary-light;

      a {
        color: $secondary-dark;
      }
    }
  }

  // Make file sizes and units line up
  .file-size-unit {
    display: inline-block;
    width: 1.5em;
  }
}
</style>
